import React, { useEffect, useState } from 'react';
import Icon from './Icon';
import SearchableSelect from './ui/SearchableSelect';
import IconButton from './ui/IconButton';
import FilterPopover, { type FilterSelectSpec, type FilterToggleSpec } from './ui/FilterPopover';

// Shared, typed action schema for the breadcrumb action bar. Extracted from
// the ~120-case switch in Header.tsx's getActionButtons(), where the same
// handful of shapes (primary button, export icon, filter select, tab
// toggle) were being hand-built per view. A page declares WHAT it needs;
// this component owns HOW it renders — so "add a delete action" or
// "make this filter toggleable" is a data change, not new JSX in a
// 3,500-line file.
//
// Renders with the exact same CSS classes the old switch used
// (header-action-btn, header-filter-select, header-toolbar-tabs, …) so
// migrating a view here is a zero-visual-diff change.

export interface BreadcrumbButtonAction {
  kind: 'primary' | 'button' | 'export' | 'workflow';
  key: string;
  label?: string;
  icon?: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
}

export interface BreadcrumbToggleAction {
  kind: 'toggle';
  key: string;
  icon: string;
  title: string;
  active: boolean;
  onClick: () => void;
  // Optional visible text — some toggles (e.g. Published/Unpublished) need
  // more than color to convey state; icon-only ones can omit it.
  label?: string;
}

export interface BreadcrumbFilterAction {
  kind: 'filter';
  key: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  // Filters are commonly hidden behind a 'toggle' action (see task: "move
  // the event filter into something we can turn on and off") rather than
  // permanently occupying breadcrumb space.
  visible?: boolean;
  /** Accessible name for the filter ("Client", "Category"): it has no visible label of its own. */
  ariaLabel?: string;
}

export interface BreadcrumbTabsAction {
  kind: 'tabs';
  key: string;
  value: string;
  onChange: (value: string) => void;
  tabs: Array<{ value: string; icon: string; title: string }>;
}

/**
 * Several filters behind one "Filters" button (FilterPopover). The bar stays one
 * row at every width, which a row of separate selects cannot promise: pick this
 * over `filter` once a page has more than one filter or a long option list.
 */
export interface BreadcrumbFiltersAction {
  kind: 'filters';
  key: string;
  selects?: FilterSelectSpec[];
  toggles?: FilterToggleSpec[];
  /** False hides the button (nothing to filter by yet, or the view it belongs to is off). */
  visible?: boolean;
}

export type BreadcrumbAction =
  | BreadcrumbButtonAction
  | BreadcrumbToggleAction
  | BreadcrumbFilterAction
  | BreadcrumbFiltersAction
  | BreadcrumbTabsAction;

/**
 * The visible text of a bar button. It is its own element so a narrow bar can
 * hide it visually (the icon stays, the accessible name stays) without
 * touching the button.
 */
const ActionLabel: React.FC<{ label?: string }> = ({ label }) =>
  label ? <span className="header-action-btn__label">{label}</span> : null;

/** Width under which separate filter selects would crowd the one-row bar. */
const NARROW_QUERY = '(max-width: 768px)';

function useNarrowBar(): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(query.matches);
    onChange();
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return narrow;
}

/** Separate `filter` selects of one bar as the specs of a single Filters popover. */
function filterSpecs(filters: BreadcrumbFilterAction[]): FilterSelectSpec[] {
  return filters.map(f => ({
    key: f.key,
    label: f.ariaLabel ?? f.key,
    options: f.options,
    value: f.value,
    defaultValue: f.options[0]?.value ?? '',
    onChange: f.onChange,
  }));
}

export function BreadcrumbBar({ actions }: { actions: BreadcrumbAction[] }): JSX.Element {
  const narrow = useNarrowBar();
  // Under 768px the separate selects become ONE Filters popover, so the bar
  // keeps a single row (trail left, actions right) instead of overflowing.
  const visibleFilters = actions.filter((a): a is BreadcrumbFilterAction => a.kind === 'filter' && a.visible !== false);
  const groupFilters = narrow && visibleFilters.length > 0;
  return (
    <>
      {actions.map(action => {
        switch (action.kind) {
          case 'filter':
            if (action.visible === false) return null;
            if (groupFilters) {
              return action === visibleFilters[0]
                ? <FilterPopover key="grouped-filters" selects={filterSpecs(visibleFilters)} />
                : null;
            }
            return (
              <div className="header-filter-group" key={action.key}>
                <SearchableSelect
                  className="header-filter-select"
                  value={action.value}
                  onChange={action.onChange}
                  options={action.options}
                  ariaLabel={action.ariaLabel}
                />
              </div>
            );

          case 'filters':
            if (action.visible === false) return null;
            return <FilterPopover key={action.key} selects={action.selects} toggles={action.toggles} />;

          case 'tabs':
            return (
              <div className="header-toolbar-tabs" key={action.key}>
                {action.tabs.map(tab => (
                  <button
                    key={tab.value}
                    className={`header-toolbar-tab${action.value === tab.value ? ' active' : ''}`}
                    onClick={() => action.onChange(tab.value)}
                    title={tab.title}
                  >
                    <Icon name={tab.icon} size={14} />
                  </button>
                ))}
              </div>
            );

          case 'toggle':
            return (
              <button
                key={action.key}
                className={`header-action-btn${action.label ? ' header-action-btn--labelled' : ''}${action.active ? ' active' : ''}`}
                onClick={action.onClick}
                title={action.title}
              >
                <Icon name={action.icon} size={16} />
                <ActionLabel label={action.label} />
              </button>
            );

          case 'primary':
          case 'button':
          case 'export':
          case 'workflow':
            // Icon with no visible text: render the shared icon button so it
            // has an accessible name (its title) and a text row on the phone.
            if (action.icon && !action.label && action.title && action.kind === 'button' && !action.danger && !action.disabled) {
              return <IconButton key={action.key} icon={action.icon} label={action.title} onClick={action.onClick} />;
            }
            return (
              <button
                key={action.key}
                className={`header-action-btn${action.icon && action.label ? ' header-action-btn--labelled' : ''}${action.kind === 'primary' || action.kind === 'workflow' ? ' primary' : ''}${action.danger ? ' header-btn-danger' : ''}`}
                onClick={action.onClick}
                disabled={action.disabled}
                title={action.title ?? action.label}
              >
                {action.icon && <Icon name={action.icon} size={16} />}
                <ActionLabel label={action.label} />
              </button>
            );

          default:
            return null;
        }
      })}
    </>
  );
}

export default BreadcrumbBar;
